import {
  FC,
  useState,
  useContext,
  useEffect,
  useRef
} from 'react';
import {
  Spin,
  Form,
  Button,
  Alert,
  Divider,
  Input,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  LoginOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AuthCredential } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';

import useFirebaseAuth from '../use/useFirebaseAuth';
import { ThemeContext } from '../providers/ThemeContext';
import FlexBox from '../components/atoms/FlexBox';
import FlexContent from '../components/atoms/FlexContent';
import FlexPage from '../components/atoms/FlexPage';
import LinkAccountModal from '../components/modals/LinkAccountModal';
import { desktopAuthRequestStore } from '../services/desktopAuth/desktopAuthRequestStore';

interface AuthError extends Error {
  code: string;
  email?: string;
  customData?: {
    email?: string;
  };
  errorInfo?: {
    email: string;
    providerId: string;
    errorCode: string;
    credential?: AuthCredential;
  };
}

/**
 * @file The only way into the app: email/password login plus Google and
 * Microsoft sign-in. Manages loading states, error display, and redirect
 * handling for social logins.
 * There is no self-service registration — an administrator creates an account,
 * or the domain gate creates one on a first social sign-in from an allowed
 * domain — so this page offers no way to register and ignores `?mode=signup`.
 */
const LoginPage: FC = () => {
  const {
    user,
    login,
    signInWithGoogle,
    signInWithMicrosoft,
    getAuthRedirectResult,
    linkAccounts,
    mapFirebaseError
  } = useFirebaseAuth();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [linkedEmail, setLinkedEmail] = useState<string>('');
  const [pendingCredential, setPendingCredential] = useState<AuthCredential | null>(null);
  const [googleLoading, setGoogleLoading] = useState<boolean>(false);
  const [microsoftLoading, setMicrosoftLoading] = useState<boolean>(false);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [isProcessingRedirect, setIsProcessingRedirect] = useState<boolean>(false);
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const isMounted = useRef(true);
  const redirectCheckPerformedRef = useRef(false);

  const anyLoading = googleLoading || microsoftLoading || formLoading || isProcessingRedirect;
  const submitLabel = formLoading ? 'Logging you in…' : 'Log in';

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * @description Sends a completed sign-in back to `/desktop-auth` whenever a
   * desktop request key is present at all — `exists`, not `read`. A sign-in
   * slower than the five-minute TTL (a Microsoft redirect with 2FA, a password
   * reset, the account-linking modal) would otherwise drop the user on `/`
   * with no sign that the desktop connection failed; `/desktop-auth` tells
   * them, in place.
   */
  useEffect(() => {
    if (user) {
      navigate(desktopAuthRequestStore.exists() ? '/desktop-auth' : '/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const handleRedirectResult = async () => {
      const currentFlag = localStorage.getItem('socialLoginRedirectPending');

      if (!redirectCheckPerformedRef.current) {
        redirectCheckPerformedRef.current = true;

        if (currentFlag === 'true') {
          if (isMounted.current) {
            setIsProcessingRedirect(true);
          }
          localStorage.removeItem('socialLoginRedirectPending');
        }

        try {
          await getAuthRedirectResult();

        } catch (e) {
          const err = e as AuthError | FirebaseError;
          const errorCode = 'code' in err ? err.code : undefined;

          let emailValue = '';
          let credential: AuthCredential | undefined;

          if ('email' in err && err.email) {
            emailValue = err.email as string;
          } else if ('customData' in err && err.customData?.email) {
            emailValue = err.customData.email as string;
          } else if ('errorInfo' in err && err.errorInfo?.email) {
            emailValue = err.errorInfo.email;
          }

          // Extract credential from error
          if ('errorInfo' in err && err.errorInfo?.credential) {
            credential = err.errorInfo.credential;
          }

          if (isMounted.current) {
            if (errorCode === 'auth/account-exists-with-different-credential' && emailValue) {
              setLinkedEmail(emailValue);
              if (credential) {
                setPendingCredential(credential);
              }
              setIsModalVisible(true);
            } else {
              setError(mapFirebaseError(err));
            }
          }
        } finally {
          setIsProcessingRedirect(false);
        }
      } else {
        try {
          await getAuthRedirectResult();
        } catch (e) {
          console.warn('LoginPage: StrictMode secondary path - error from getAuthRedirectResult():', e);
        }
      }
    };

    handleRedirectResult();
  }, [getAuthRedirectResult, mapFirebaseError, setError, setLinkedEmail, setIsModalVisible, setIsProcessingRedirect]);

  /**
   * @description Handles user login with email and password.
   */
  const handleLogin = async () => {
    setFormLoading(true);
    setError(null);
    try {
      await login(email, password);
    } catch (e) {
      const err = e as any;
      const errorCode = err.code || err.errorInfo?.errorCode;
      const emailValue = err.email || err.customData?.email || err.errorInfo?.email;

      if (errorCode === 'auth/account-exists-with-different-credential' && emailValue) {
        setLinkedEmail(emailValue);
        setIsModalVisible(true);
      } else {
        const mappedError = mapFirebaseError(err);
        setError(mappedError);
      }
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * @description Initiates sign-in process with Google.
   */
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      localStorage.setItem('socialLoginRedirectPending', 'true');
      await signInWithGoogle();
    } catch (e) {
      localStorage.removeItem('socialLoginRedirectPending');
      console.error('LoginPage: Google sign-in initiation failed', e);
      if (isMounted.current) {
        setError(mapFirebaseError(e));
        setGoogleLoading(false);
      }
    }
  };

  /**
   * @description Initiates sign-in process with Microsoft.
   */
  const handleMicrosoftSignIn = async () => {
    setMicrosoftLoading(true);
    setError(null);
    try {
      localStorage.setItem('socialLoginRedirectPending', 'true');
      await signInWithMicrosoft();
    } catch (e) {
      localStorage.removeItem('socialLoginRedirectPending');
      console.error('LoginPage: Microsoft sign-in initiation failed', e);
      if (isMounted.current) {
        setError(mapFirebaseError(e));
        setMicrosoftLoading(false);
      }
    }
  };

  /**
   * @description Handles the submission of the account linking form.
   * @param values - The form values containing email and password.
   */
  const handleLinkFormSubmit = async (values: { email: string; password: string }) => {
    setFormLoading(true);
    setError(null);

    try {
      if (pendingCredential) {
        // Use linkAccounts to properly link the social account with the existing account
        await linkAccounts(values.email, values.password, pendingCredential);

        // Clear the pending credential and close modal on success
        setPendingCredential(null);
        setIsModalVisible(false);
      } else {
        // Fallback to regular login if no credential is available
        // This shouldn't happen in normal flow, but provides a fallback
        setEmail(values.email);
        setPassword(values.password);
        await handleLogin();
      }
    } catch (e) {
      const err = e as any;
      setError(mapFirebaseError(err));
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <FlexPage>
      <FlexContent style={{
        alignContent: 'center',
        justifyContent: 'center'
      }}>
        {(user || isProcessingRedirect) ? (
          <Spin size='large' />
        ) : (
          <FlexBox
            column
            noGrow
            gap={0}
            style={{
              maxWidth: '290px'
            }}
          >
            <img
              src={darkMode ? '/art/Syncretic-logo-vertical-negative.svg' : '/art/Syncretic-logo-vertical.svg'}
              alt='Syncretic'
              style={{
                marginTop: 20,
                marginBottom: 50,
                width: '100%',
              }}
            />
            <FlexBox
              column
              style={{
                textAlign: 'center',
                width: '100%',
              }}
            >
              <Button
                onClick={handleGoogleSignIn}
                loading={googleLoading}
                disabled={anyLoading}
                size='large'
                style={{
                  width: '100%',
                  backgroundColor: '#444444',
                  color: '#fff',
                  border: 'none',
                  textAlign: 'center',
                }}
                icon={<img
                  src='/art/Google__G__Logo.svg'
                  alt="Google sign-in"
                  style={{
                    paddingRight: 10,
                    height: 20,
                    marginTop: 5,
                  }}
                />}
              >
                Sign in with Google
              </Button>
              <Button
                onClick={handleMicrosoftSignIn}
                loading={microsoftLoading}
                disabled={anyLoading}
                size='large'
                style={{
                  width: '100%',
                  backgroundColor: '#444444',
                  color: '#fff',
                  border: 'none',
                  textAlign: 'center',
                }}
                icon={<img
                  src='/art/Microsoft_logo.svg'
                  alt="Microsoft sign-in"
                  style={{
                    paddingRight: 10,
                    height: 20,
                    marginTop: 5,
                  }}
                />}
              >
                Sign in with Microsoft
              </Button>
              <Divider>
                OR
              </Divider>
              <Form
                name="login_form"
                onFinish={handleLogin}
                style={{ width: '100%' }}
                initialValues={{ email, password }}
              >
                <Form.Item
                  name="email"
                  rules={[{ required: true, message: 'Please input your email!' }]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'Please input your password!' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="link"
                    size='small'
                    onClick={() => navigate('/password-reset')}
                    disabled={anyLoading}
                    style={{ width: '100%' }}
                  >
                    Reset password
                  </Button>
                </Form.Item>
                {error && (
                  <Form.Item>
                    <Alert
                      title={error}
                      type='error'
                      showIcon
                    />
                  </Form.Item>
                )}
                <Form.Item>
                  <Button
                    style={{ width: '100%' }}
                    loading={formLoading}
                    htmlType="submit"
                    disabled={anyLoading || !email || !password}
                    icon={<LoginOutlined />}
                  >
                    {submitLabel}
                  </Button>
                </Form.Item>
              </Form>
            </FlexBox>
          </FlexBox>
        )}
        <LinkAccountModal
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setPendingCredential(null);
            setError(null);
          }}
          linkedEmail={linkedEmail}
          onFormSubmit={handleLinkFormSubmit}
          isLoading={anyLoading}
          error={error}
        />
      </FlexContent>
    </FlexPage>
  );
}

export default LoginPage;
